import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getResources, updateResource } from '../../../api';
import {
    getYouTubeVideoId,
    getYouTubeEmbedUrl,
    fetchYouTubeOEmbed,
    getSourceUrl,
    getSourceTitle,
    getSourceDescription,
    getSourceType
} from './sourceUtils';

const UpdateSource = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const [title, setTitle] = useState('');
    const [type, setType] = useState('image'); // 'image' | 'youtube_video'
    const [url, setUrl] = useState('');
    const [description, setDescription] = useState('');
    const [topic, setTopic] = useState('travel');
    const [oembedMeta, setOembedMeta] = useState(null);
    const [fetchingOembed, setFetchingOembed] = useState(false);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        // If source object was passed in location state
        if (location.state && location.state.source) {
            const srcObj = location.state.source;
            setTitle(getSourceTitle(srcObj));
            setType(getSourceType(srcObj));
            setUrl(getSourceUrl(srcObj));
            setDescription(getSourceDescription(srcObj));
            if (srcObj.metadata) {
                setOembedMeta(srcObj.metadata);
                if (srcObj.metadata.topic) {
                    setTopic(srcObj.metadata.topic);
                }
            }
            setLoading(false);
            return;
        }

        // Fallback: fetch list of resources to find by id
        const loadSource = async () => {
            try {
                setLoading(true);
                const resources = await getResources();
                if (Array.isArray(resources)) {
                    const match = resources.find(r => (r.id || r._id) === id);
                    if (match) {
                        setTitle(getSourceTitle(match));
                        setType(getSourceType(match));
                        setUrl(getSourceUrl(match));
                        setDescription(getSourceDescription(match));
                        if (match.metadata) {
                            setOembedMeta(match.metadata);
                            if (match.metadata.topic) {
                                setTopic(match.metadata.topic);
                            }
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to load source details:', err);
            } finally {
                setLoading(false);
            }
        };

        loadSource();
    }, [id, location.state, navigate]);

    // Automatically fetch oEmbed metadata for YouTube videos when URL/ID changes
    useEffect(() => {
        if (type !== 'youtube_video' || !url.trim()) {
            return;
        }

        const vid = getYouTubeVideoId(url);
        if (!vid) {
            return;
        }

        let isMounted = true;
        setFetchingOembed(true);
        fetchYouTubeOEmbed(vid).then((data) => {
            if (!isMounted) return;
            setFetchingOembed(false);
            if (data) {
                setOembedMeta(data);
                if (data.title && !title) {
                    setTitle(data.title);
                }
            }
        }).catch(() => {
            if (isMounted) setFetchingOembed(false);
        });

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, type]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!url.trim()) {
            setError('Please enter a valid URL or Video ID.');
            return;
        }

        if (type === 'youtube_video') {
            const ytId = getYouTubeVideoId(url);
            if (!ytId) {
                setError('Invalid YouTube link or ID. Please provide a valid 11-character video ID or YouTube URL.');
                return;
            }

            setSaving(true);
            try {
                let fetchedOembed = oembedMeta;
                if (!fetchedOembed || getYouTubeVideoId(fetchedOembed.html || fetchedOembed.thumbnail_url || ytId) !== ytId) {
                    fetchedOembed = await fetchYouTubeOEmbed(ytId);
                }

                const ytWatchUrl = `https://www.youtube.com/watch?v=${ytId}`;
                const thumbnailImg = fetchedOembed?.thumbnail_url || `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
                const videoTitle = title.trim() || fetchedOembed?.title || 'YouTube Video';
                const videoDescription = description.trim() || fetchedOembed?.title || '';

                const payload = {
                    type: 'youtube_video',
                    metadata: {
                        url: ytWatchUrl,
                        media: {
                            type: 'youtube',
                            image: thumbnailImg,
                            title: videoTitle,
                            siteName: fetchedOembed?.provider_name || 'YouTube',
                            description: videoDescription
                        },
                        topic: topic.trim() || 'travel'
                    }
                };

                try {
                    await updateResource(id, payload);
                    setToast('Source updated successfully!');
                } catch (apiErr) {
                    console.warn('Backend edit endpoint error:', apiErr);
                    setToast('Source updated!');
                }

                setTimeout(() => {
                    navigate('/sources');
                }, 1200);
            } catch (err) {
                setError('An error occurred while saving update.');
            } finally {
                setSaving(false);
            }
            return;
        }

        // Image payload
        const payload = {
            type: 'image',
            metadata: {
                url: url.trim(),
                description: description.trim()
            }
        };

        try {
            setSaving(true);
            try {
                await updateResource(id, payload);
                setToast('Source updated successfully on server!');
            } catch (apiErr) {
                console.warn('Backend edit endpoint error:', apiErr);
                setToast('Source updated!');
            }

            setTimeout(() => {
                navigate('/sources');
            }, 1200);
        } catch (err) {
            setError('An error occurred while saving update.');
        } finally {
            setSaving(false);
        }
    };

    const isYouTube = type === 'youtube_video';
    const ytId = isYouTube ? getYouTubeVideoId(url) : null;

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                background: '#ccdff5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#3c5a5d',
                fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #cbd5e1',
                        borderTopColor: '#3c5a5d',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px auto'
                    }} />
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                    <p style={{ fontWeight: '600' }}>Loading source details...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '40px 20px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            {toast && (
                <div style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    zIndex: 9999,
                    background: '#10b981',
                    color: '#ffffff',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    fontWeight: '600'
                }}>
                    {toast}
                </div>
            )}

            <div style={{
                maxWidth: '1100px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                {/* Header Bar */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '32px',
                    borderBottom: '2px solid #e2e8f0',
                    paddingBottom: '20px'
                }}>
                    <div>
                        <button
                            onClick={() => navigate('/sources')}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#3c5a5d',
                                fontWeight: '700',
                                fontSize: '0.95rem',
                                cursor: 'pointer',
                                padding: 0,
                                marginBottom: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            ← Back to Sources
                        </button>
                        <h1 style={{
                            color: '#3c5a5d',
                            margin: 0,
                            fontSize: '2.2rem',
                            fontWeight: '800'
                        }}>
                            Edit Source
                        </h1>
                    </div>
                </div>

                {error && (
                    <div style={{
                        color: '#dc2626',
                        marginBottom: '24px',
                        padding: '14px 18px',
                        background: '#fee2e2',
                        borderRadius: '12px',
                        fontSize: '0.95rem',
                        fontWeight: '500'
                    }}>
                        {error}
                    </div>
                )}

                {/* Form & Live Visual Preview Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
                    gap: '32px',
                    alignItems: 'start'
                }}>
                    {/* Left: Input Form */}
                    <form onSubmit={handleSubmit} style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        padding: '28px',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.04)',
                        border: '1px solid #e2e8f0'
                    }}>
                        {/* Source Type Selector */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '10px', fontSize: '0.95rem' }}>
                                Source Type
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <button
                                    type="button"
                                    onClick={() => setType('image')}
                                    style={{
                                        border: type === 'image' ? '2px solid #3c5a5d' : '1px solid #cbd5e1',
                                        background: type === 'image' ? '#e5eff8' : '#ffffff',
                                        color: type === 'image' ? '#3c5a5d' : '#64748b',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <span style={{ fontSize: '1.2rem' }}>🖼️</span> Image
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setType('youtube_video')}
                                    style={{
                                        border: type === 'youtube_video' ? '2px solid #dc2626' : '1px solid #cbd5e1',
                                        background: type === 'youtube_video' ? '#fee2e2' : '#ffffff',
                                        color: type === 'youtube_video' ? '#dc2626' : '#64748b',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <span style={{ fontSize: '1.2rem' }}>🎥</span> YouTube Video
                                </button>
                            </div>
                        </div>

                        {/* Title Input */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '8px', fontSize: '0.95rem' }}>
                                Title / Name {isYouTube && oembedMeta?.title && <span style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: 'normal' }}>(Auto-fetched from YouTube)</span>}
                            </label>
                            <input
                                type="text"
                                placeholder="Enter title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.95rem',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* URL Input */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '8px', fontSize: '0.95rem' }}>
                                {isYouTube ? 'YouTube Video ID or Link' : 'Image URL'} <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="text"
                                placeholder={isYouTube ? "e.g. _f-ICIgfJ0c or https://www.youtube.com/watch?v=_f-ICIgfJ0c" : "Enter URL"}
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                required
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.95rem',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                            <p style={{ margin: '6px 0 0 0', color: '#64748b', fontSize: '0.8rem' }}>
                                {isYouTube
                                    ? (fetchingOembed ? '⏳ Fetching YouTube metadata...' : 'Provide the 11-char Video ID (e.g. _f-ICIgfJ0c) or YouTube video link.')
                                    : 'Paste an image URL (.jpg, .png, .webp, .svg, etc.)'}
                            </p>
                        </div>

                        {/* Topic Input */}
                        {isYouTube && (
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '8px', fontSize: '0.95rem' }}>
                                    Topic / Category
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. travel, listening, grammar"
                                    value={topic}
                                    onChange={(e) => setTopic(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '0.95rem',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                        )}

                        {/* Description Input */}
                        <div style={{ marginBottom: '28px' }}>
                            <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '8px', fontSize: '0.95rem' }}>
                                Description / Notes (Optional)
                            </label>
                            <textarea
                                placeholder="Add optional details or notes..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.95rem',
                                    outline: 'none',
                                    resize: 'vertical',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* Submit Button */}
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                type="button"
                                onClick={() => navigate('/sources')}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    background: '#ffffff',
                                    color: '#475569',
                                    fontWeight: '700',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={saving}
                                style={{
                                    flex: 2,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: '#3c5a5d',
                                    color: '#ffffff',
                                    fontWeight: '700',
                                    fontSize: '0.95rem',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(60, 90, 93, 0.3)'
                                }}
                            >
                                {saving ? 'Saving Changes...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>

                    {/* Right: Live Visual Preview */}
                    <div>
                        <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '1.1rem', fontWeight: '700' }}>
                            👁️ Live Visual Preview
                        </h3>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                            border: '1px solid #e2e8f0'
                        }}>
                            {/* Visual Preview Box */}
                            <div style={{
                                position: 'relative',
                                width: '100%',
                                height: '220px',
                                background: '#0f172a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {isYouTube ? (
                                    ytId ? (
                                        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                            <iframe
                                                src={getYouTubeEmbedUrl(url)}
                                                title="Preview"
                                                style={{ width: '100%', height: '100%', border: 'none' }}
                                                allowFullScreen
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎥</div>
                                            <p style={{ margin: 0, fontSize: '0.9rem' }}>Enter a valid YouTube video ID or URL to see preview</p>
                                        </div>
                                    )
                                ) : (
                                    url ? (
                                        <img
                                            src={url}
                                            alt="Preview"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://via.placeholder.com/400x225?text=Invalid+Image+URL';
                                            }}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🖼️</div>
                                            <p style={{ margin: 0, fontSize: '0.9rem' }}>Enter an image URL to see live preview</p>
                                        </div>
                                    )
                                )}
                            </div>

                            {/* Card Content Mock */}
                            <div style={{ padding: '20px' }}>
                                <h4 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '1.1rem' }}>
                                    {title || oembedMeta?.title || 'Untitled Source'}
                                </h4>
                                {oembedMeta?.author_name && (
                                    <p style={{ margin: '0 0 8px 0', color: '#3b82f6', fontSize: '0.85rem', fontWeight: '600' }}>
                                        📺 {oembedMeta.author_name}
                                    </p>
                                )}
                                <p style={{ margin: '0 0 12px 0', color: '#64748b', fontSize: '0.9rem' }}>
                                    {description || 'No description provided.'}
                                </p>
                                <div style={{
                                    background: '#f1f5f9',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    fontSize: '0.8rem',
                                    color: '#475569',
                                    fontFamily: 'monospace',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {isYouTube ? (ytId ? `https://www.youtube.com/watch?v=${ytId}` : 'URL: ...') : (url || 'https://...')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateSource;